import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { Modal, Button, Form, Spinner, Alert } from 'react-bootstrap';
import { ChevronDown, ChevronRight } from 'lucide-react';
import { useBuildComponentTypes } from '../../contexts/BuildComponentTypesContext';
import { maintenanceTemplateApi } from '../../api/maintenanceTemplateApi';
import { translate } from '../../types/EnumTranslations';
import { isTemplateArchived } from '../../Helpers/maintenanceTemplates';

interface Props {
    show: boolean;
    onHide: () => void;
    targetTypeKey: string;
    onCopyComplete: (count: number) => void;
}

interface SelectedSource {
    key: string;
    name: string;
    templateCount: number;
}

/**
 * Modal for copying maintenance templates from another component type.
 * Shows a flat, searchable list of component types that have templates; types
 * without templates are listed, not selectable, in a collapsed section. By default only types sharing the target's baseType are listed; a toggle
 * widens the list to all types. The confirmation step lists the source type's
 * templates as checkboxes (all ticked by default) so only a subset can be copied.
 */
export const CopyTemplatesFromTypeModal: React.FC<Props> = ({
    show,
    onHide,
    targetTypeKey,
    onCopyComplete
}) => {
    const { types, getTemplates, getBaseType } = useBuildComponentTypes();
    // Archived templates are never copied, nor do their names block a copy
    const liveTemplates = useCallback((key: string) => getTemplates(key).filter(t => !isTemplateArchived(t)), [getTemplates]);
    const [searchTerm, setSearchTerm] = useState('');
    const [showAllTypes, setShowAllTypes] = useState(false);
    const [isCopying, setIsCopying] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [selectedSource, setSelectedSource] = useState<SelectedSource | null>(null);
    const [selectedTemplateIds, setSelectedTemplateIds] = useState<Set<string>>(new Set());
    // null = not toggled by the user yet; the section then follows the search
    const [showTypesWithoutTemplates, setShowTypesWithoutTemplates] = useState<boolean | null>(null);

    // Reset state when modal opens
    useEffect(() => {
        if (show) {
            setSearchTerm('');
            setShowAllTypes(false);
            setShowTypesWithoutTemplates(null);
            setError(null);
            setIsCopying(false);
            setSelectedSource(null);
            setSelectedTemplateIds(new Set());
        }
    }, [show]);

    // Default to types with the same baseType as the target. Targets without a
    // baseType can't be narrowed, so they always see the full list.
    const targetBaseType = getBaseType(targetTypeKey);
    const filterByBaseType = !!targetBaseType && !showAllTypes;

    // Other types filtered by baseType and search, split into those with templates
    // (selectable) and those without (listed separately so they don't look missing)
    const { availableTypes, typesWithoutTemplates } = useMemo(() => {
        const matching = Object.entries(types)
            .filter(([key]) => key !== targetTypeKey)
            .filter(([_, type]) => !filterByBaseType || type.baseType === targetBaseType)
            .map(([key, type]) => ({
                key,
                name: type.translations?.sv || key,
                baseType: type.baseType,
                templateCount: liveTemplates(key).length
            }))
            .filter(type =>
                type.name.toLowerCase().includes(searchTerm.toLowerCase())
            )
            .sort((a, b) => a.name.localeCompare(b.name, 'sv'));
        return {
            availableTypes: matching.filter(type => type.templateCount > 0),
            typesWithoutTemplates: matching.filter(type => type.templateCount === 0)
        };
    }, [types, targetTypeKey, targetBaseType, filterByBaseType, searchTerm, liveTemplates]);

    // Until the user toggles it, a search that only hits types without templates
    // opens the section, so the match is visible instead of "not found"
    const emptyTypesExpanded = showTypesWithoutTemplates
        ?? (!!searchTerm && availableTypes.length === 0);

    const emptyMessage = searchTerm
        ? 'Inga komponenttyper med mallar hittades'
        : filterByBaseType
            ? `Inga andra typer med bastypen ${translate(targetBaseType!)} har mallar`
            : 'Inga andra typer har mallar';

    const handleSelect = (source: SelectedSource) => {
        setSelectedSource(source);
        // Every template starts ticked; the user unticks the ones to skip
        setSelectedTemplateIds(new Set(liveTemplates(source.key).map(t => t.id)));
    };

    const toggleTemplate = (id: string) => {
        setSelectedTemplateIds(prev => {
            const next = new Set(prev);
            if (next.has(id)) {
                next.delete(id);
            } else {
                next.add(id);
            }
            return next;
        });
    };

    const handleConfirmCopy = async () => {
        if (!selectedSource) return;

        setIsCopying(true);
        setError(null);

        try {
            const sourceTemplates = liveTemplates(selectedSource.key)
                .filter(t => selectedTemplateIds.has(t.id));
            const targetTemplates = liveTemplates(targetTypeKey);
            const existingNames = new Set(targetTemplates.map(t => t.name));

            const count = await maintenanceTemplateApi.copyTemplates(
                sourceTemplates,
                targetTypeKey,
                existingNames
            );

            onCopyComplete(count);
            onHide();
        } catch (err) {
            setError('Kunde inte kopiera mallar');
            // Keep confirmation view visible so user sees the error
        } finally {
            setIsCopying(false);
        }
    };

    const handleBack = () => {
        setSelectedSource(null);
        setError(null);
    };

    // Confirmation view
    if (selectedSource) {
        const sourceTemplates = liveTemplates(selectedSource.key);
        const selectedCount = sourceTemplates.filter(t => selectedTemplateIds.has(t.id)).length;
        const allSelected = selectedCount === sourceTemplates.length;
        const label = selectedCount === 1 ? 'mall' : 'mallar';
        return (
            <Modal show={show} onHide={onHide} centered>
                <Modal.Header closeButton>
                    <Modal.Title>Bekräfta kopiering</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    {error && <Alert variant="danger">{error}</Alert>}

                    {isCopying ? (
                        <div className="text-center py-4">
                            <Spinner animation="border" size="sm" />
                            <span className="ms-2">Kopierar mallar...</span>
                        </div>
                    ) : (
                        <>
                            <p>
                                Vill du kopiera {selectedCount} {label} från <strong>{selectedSource.name}</strong>?
                            </p>
                            <Form.Check
                                type="checkbox"
                                id="copy-templates-select-all"
                                label="Markera alla"
                                checked={allSelected}
                                ref={(el: HTMLInputElement | null) => {
                                    if (el) el.indeterminate = selectedCount > 0 && !allSelected;
                                }}
                                onChange={() => setSelectedTemplateIds(
                                    allSelected ? new Set() : new Set(sourceTemplates.map(t => t.id))
                                )}
                                className="mb-2 small"
                            />
                            <div className="border rounded p-2" style={{ maxHeight: '300px', overflowY: 'auto' }}>
                                {sourceTemplates.map(template => (
                                    <Form.Check
                                        key={template.id}
                                        type="checkbox"
                                        id={`copy-template-${template.id}`}
                                        label={template.name}
                                        checked={selectedTemplateIds.has(template.id)}
                                        onChange={() => toggleTemplate(template.id)}
                                        className="small"
                                    />
                                ))}
                            </div>
                        </>
                    )}
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={handleBack} disabled={isCopying}>
                        Tillbaka
                    </Button>
                    <Button variant="primary" onClick={handleConfirmCopy} disabled={isCopying || selectedCount === 0}>
                        Kopiera
                    </Button>
                </Modal.Footer>
            </Modal>
        );
    }

    // Selection view
    return (
        <Modal show={show} onHide={onHide} centered>
            <Modal.Header closeButton>
                <Modal.Title>Kopiera mallar från annan typ</Modal.Title>
            </Modal.Header>
            <Modal.Body>
                {error && <Alert variant="danger">{error}</Alert>}

                <Form.Control
                    type="text"
                    placeholder="Sök komponenttyp..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="mb-2"
                />

                {targetBaseType && (
                    <Form.Check
                        type="switch"
                        id="copy-templates-show-all-types"
                        label={`Visa alla typer (inte bara ${translate(targetBaseType)})`}
                        checked={showAllTypes}
                        onChange={(e) => setShowAllTypes(e.target.checked)}
                        className="mb-3"
                    />
                )}

                <div style={{ maxHeight: '400px', overflowY: 'auto' }}>
                    {availableTypes.map(type => (
                        <button
                            key={type.key}
                            className="btn btn-outline-secondary btn-sm w-100 text-start mb-1 d-flex justify-content-between align-items-center"
                            onClick={() => handleSelect(type)}
                        >
                            <span>{type.name}</span>
                            <span className="text-muted small text-nowrap ms-2">
                                {!filterByBaseType && type.baseType && `${translate(type.baseType)} · `}
                                {type.templateCount} {type.templateCount === 1 ? 'mall' : 'mallar'}
                            </span>
                        </button>
                    ))}

                    {availableTypes.length === 0 && (
                        <p className="text-muted text-center">{emptyMessage}</p>
                    )}

                    {typesWithoutTemplates.length > 0 && (
                        <div className="mt-3 border-top pt-2">
                            <button
                                type="button"
                                className="btn btn-link btn-sm p-0 text-decoration-none d-flex align-items-center"
                                onClick={() => setShowTypesWithoutTemplates(!emptyTypesExpanded)}
                                aria-expanded={emptyTypesExpanded}
                            >
                                {emptyTypesExpanded ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
                                <span className="ms-1">Typer utan mallar ({typesWithoutTemplates.length})</span>
                            </button>
                            <p className="text-muted small mb-2 mt-1">
                                Dessa typer har inga mallar ännu och kan därför inte kopieras från.
                            </p>
                            {emptyTypesExpanded && (
                                <ul className="list-unstyled mb-0">
                                    {typesWithoutTemplates.map(type => (
                                        <li
                                            key={type.key}
                                            className="text-muted small px-2 py-1 d-flex justify-content-between"
                                        >
                                            <span>{type.name}</span>
                                            {!filterByBaseType && type.baseType && (
                                                <span>{translate(type.baseType)}</span>
                                            )}
                                        </li>
                                    ))}
                                </ul>
                            )}
                        </div>
                    )}
                </div>
            </Modal.Body>
            <Modal.Footer>
                <Button variant="secondary" onClick={onHide}>
                    Avbryt
                </Button>
            </Modal.Footer>
        </Modal>
    );
};
